<script lang="ts" setup>
import { PERIOD_SHORTCUT_BY_FREQUENCY } from "@modules/energy-manager/constants"
import { findMatchingLastYearWeekPeriod } from "@modules/energy-manager/helpers"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { Freq } from "@types"
import { useVModel } from "@vueuse/core"
import moment from "moment"
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"

type Props = {
    choice: string
    period: [Date | null, Date | null]
    compare: string
    freq: Freq
    disabled?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(["update:compare", "update:dates"])

const emConsoCompareStore = useEmConsoCompareStore()

// Comparer avec une période précédente ou de l'année précédente
const compare = useVModel(props, "compare", emit)

const { t } = useI18n({
    messages: {
        fr: {
            compare: {
                button: "Par rapport à",
                period_before: ({
                    named,
                }: {
                    named: (name: string) => string
                }) => {
                    const mappingUnit = {
                        days: "jour",
                        months: "mois",
                        weeks: "semaine",
                        years: "année",
                    } as Record<string, string>

                    const mapping = {
                        custom: `${named("period")} ${mappingUnit[named("unit")]} précédents`,
                        current_week: `${named("period")} jours précédents`,
                        last_7days: "7 jours précédents",
                        last_30days: "30 jours précédents",
                        last_1week: "Semaine précédente",
                        last_2weeks: "2 semaines précédentes",
                        last_4weeks: "4 semaines précédentes",
                        last_12weeks: "12 semaines précédentes",
                        last_1month: "Mois précédent",
                        last_3months: "3 mois précédents",
                        last_6months: "6 mois précédents",
                        last_12months: "12 mois précédents",
                        last_1year: "Année précédente",
                        last_3years: "3 années précédentes",
                        last_5years: "5 années précédentes",
                    } as Record<string, string>

                    return mapping[props.choice] ?? ""
                },
                year_period_before: ({
                    named,
                }: {
                    named: (name: string) => string
                }) => {
                    const mappingUnit = {
                        days: "jour",
                        months: "mois",
                        weeks: "semaine",
                        years: "année",
                    } as Record<string, string>
                    const mapping = {
                        custom: `${named("period")} même ${mappingUnit[named("unit")]}  de l'année précédente`,
                        current_week: `${named("period")} même jours de l'année précédente`,
                        last_1week: "Même semaine de l'année précédente",
                        last_2weeks: "2 même semaines de l'année précédente",
                        last_4weeks: "4 même semaines de l'année précédente",
                        last_12weeks: "12 même semaines de l'année précédente",
                        last_7days: "7 même jours de l'année précédente",
                        last_30days: "30 même jours de l'année précédente",
                        last_1month: "Même mois de l'année précédente",
                        last_3months: "3 même mois de l'année précédente",
                        last_6months: "6 même mois de l'année précédente",
                        last_12months: "12 même mois de l'année précédente",
                    } as Record<string, string>

                    return mapping[props.choice] ?? ""
                },
            },
        },
    },
})

/**
 * Paramètre "shortcuts" (liste des raccourcis de sélection de période) en fonction de la fréquence
 */
const getShortcutsPeriodsList = computed(() => {
    return PERIOD_SHORTCUT_BY_FREQUENCY[props.freq].map((option) => {
        return {
            value: option.id,
            label: t(`shortcut_period.${option.id}`),
            disabled: (
                emConsoCompareStore.getDisplayDataConfig?.config_periods[
                    props.freq
                ]?.disabled_shortcut ?? []
            ).includes(option.id),
            period: option.period,
        }
    })
})

function calculatePeriodBefore(
    startDate: Date,
    endDate: Date,
    quantity: number = 1,
    unit: moment.unitOfTime.Diff = "days"
) {
    const startDateBefore = moment(startDate).subtract(quantity, unit)

    const endDateBefore = moment(endDate).subtract(quantity, unit)

    return { startDateBefore, endDateBefore }
}

/**
 * Calcule de la période précédente par rapport à la fréquence
 * @param startDate - Date de début
 * @param endDate - Date de fin
 * @param freq - Fréquence
 */
function calculateCustomPeriodBefore(
    startDate: Date,
    endDate: Date,
    freq: Freq
) {
    let diff = 0
    let unit: moment.unitOfTime.Diff = "days"

    switch (freq) {
        case "monthly":
            unit = "months"
            break
        case "weekly":
            unit = "weeks"
            break
        // case "yearly":
        //     unit = "years"
        //     break
        default:
            unit = "days"
            break
    }

    diff = moment(endDate).diff(moment(startDate), unit) + 1

    const { startDateBefore, endDateBefore } = calculatePeriodBefore(
        startDate,
        endDate,
        diff,
        unit
    )
    return { startDateBefore, endDateBefore, diff, unit }
}

/**
 * Liste des options de comparaison en fonction de la période sélectionné
 */
const getCompareOptions = computed(() => {
    const periodConfig = getShortcutsPeriodsList.value.find(
        (item) => item.value === props.choice
    )?.period

    if (!periodConfig) return []

    const [startDate, endDate] = props.period

    let startDateBefore
    let endDateBefore
    let diff = 0
    let unit = "days"

    const isFreqHourlyDaily = ["hourly", "daily"].includes(props.freq)

    // Si le choix de période est "custom", on calcule la période précédente par rapport à la fréquence
    if (props.choice === "custom") {
        ;({ startDateBefore, endDateBefore, diff, unit } =
            calculateCustomPeriodBefore(startDate!, endDate!, props.freq))
    }
    // Si le choix de période est "current_week", on calcule la période précédente par rapport à la fréquence
    else if (props.choice === "current_week") {
        ;({ startDateBefore, endDateBefore, diff, unit } =
            calculateCustomPeriodBefore(startDate!, endDate!, "daily"))
    }
    // Si le choix de période est un raccourci qui inclue "day" comme "last_7days" ou qu'on est en fréquence "hourly" ou "daily"
    else if (props.choice.includes("day") || isFreqHourlyDaily) {
        // On calcule la période précédente en fonction de la période actuelle en comptant le nombre de jours
        const startDateMoment = moment(startDate)
        const endDateMoment = moment(endDate)
        const quantity = endDateMoment.diff(startDateMoment, "days") + 1

        ;({ startDateBefore, endDateBefore } = calculatePeriodBefore(
            startDate!,
            endDate!,
            quantity,
            "days"
        ))
    }
    // Si le choix de période est un raccourci qui inclue "month" comme "last_1month" et qu'on n'est pas en fréquence "hourly" ou "daily"
    else if (props.choice.includes("month") && !isFreqHourlyDaily) {
        ;({ startDateBefore, endDateBefore } = calculatePeriodBefore(
            startDate!,
            endDate!,
            periodConfig.quantity,
            "month"
        ))
    }
    // Si le choix de période est un raccourci qui inclue "week" comme "last_1week" et qu'on n'est pas en fréquence "hourly" ou "daily"
    else if (props.choice.includes("week") && !isFreqHourlyDaily) {
        ;({ startDateBefore, endDateBefore } = calculatePeriodBefore(
            startDate!,
            endDate!,
            periodConfig.quantity,
            "week"
        ))
    }
    // Si le choix de période est un raccourci qui inclue "year" comme "last_1year" et qu'on n'est pas en fréquence "hourly" ou "daily"
    else if (props.choice.includes("year") && !isFreqHourlyDaily) {
        ;({ startDateBefore, endDateBefore } = calculatePeriodBefore(
            startDate!,
            endDate!,
            periodConfig.quantity,
            "year"
        ))
    }

    const options = [
        {
            label: t("compare.period_before", { period: diff, unit }),
            date: `Du ${startDateBefore?.format("DD/MM/YYYY")} au ${endDateBefore?.format("DD/MM/YYYY")}`,
            value: "before",
            value_dates: [startDateBefore, endDateBefore],
        },
    ]

    if (props.freq !== "yearly") {
        // Vérifie si la période actuelle est une année bissextile
        const isLeapYear = moment(startDate).isLeapYear()

        let startDateBeforeYear
        let endDateBeforeYear

        // Dans le cas d'une fréquence "weekly", on cherche la période correspondante de l'année précédente
        if (props.freq === "weekly") {
            const matchingPeriod = findMatchingLastYearWeekPeriod(
                startDate!,
                endDate!
            )
            startDateBeforeYear = moment(matchingPeriod[0])
            endDateBeforeYear = moment(matchingPeriod[1])
        } else {
            ;({
                startDateBefore: startDateBeforeYear,
                endDateBefore: endDateBeforeYear,
            } = calculatePeriodBefore(
                startDate!,
                endDate!,
                isFreqHourlyDaily ? (isLeapYear ? 366 : 365) : 1,
                isFreqHourlyDaily ? "days" : "years"
            ))
        }

        options.push({
            label: t("compare.year_period_before", { period: diff, unit }),
            date: `Du ${startDateBeforeYear?.format("DD/MM/YYYY")} au ${endDateBeforeYear?.format("DD/MM/YYYY")}`,
            value: "before_year",
            value_dates: [startDateBeforeYear, endDateBeforeYear],
        })
    }

    return options
})

watch(
    getCompareOptions,
    () => {
        if (
            getCompareOptions.value.length === 1 &&
            compare.value === "before_year"
        ) {
            compare.value = "before"
        }

        emit(
            "update:dates",
            getCompareOptions.value.find((item) => item.value === compare.value)
                ?.value_dates
        )
    },
    {
        deep: true,
        immediate: true,
    }
)

function onUpdateValue($event: any) {
    compare.value = $event
    emit(
        "update:dates",
        getCompareOptions.value.find((item) => item.value === $event)
            ?.value_dates
    )
}
</script>

<template>
    <BaseInputWrapper
        input-id="compare_period"
        class="dashboard-card-compare-period-compare"
    >
        <BaseSelect
            input-id="compare_period"
            :model-value="compare"
            :options="getCompareOptions"
            option-label="label"
            option-value="value"
            :disabled="!compare || disabled"
            :highlight-on-select="false"
            @update:model-value="onUpdateValue"
        >
            <template #option="{ option, selected }">
                <div
                    :class="
                        selected
                            ? 'date-option-label-focus'
                            : 'date-option-label'
                    "
                >
                    <p>
                        <i v-if="selected" class="pi pi-check"></i>
                        {{ option.label }}
                    </p>
                    <span>{{ option.date }}</span>
                </div>
            </template>
        </BaseSelect>
    </BaseInputWrapper>
</template>

<style>
.date-option-label,
.date-option-label-focus {
    display: flex;
    align-items: center;
    gap: 2.25rem;
    font-weight: 600;
    justify-content: space-between;
    width: 100%;
}
.date-option-label {
    margin-left: 20px;
    color: #616161;
}
.date-option-label-focus {
    color: var(--p-cyan-400);
}
.date-option-label span,
.date-option-label-focus span {
    font-weight: 400;
    color: #757575 !important;
}
</style>
